/**
 * MED-1195 (D5: Tag-Based Display) — map an AI-note "Based on" source trace to
 * the client-approved category label.
 *
 * Background: the AI emits a free-text source trace per note ("Jordan's
 * primary_goal", "session_anatomy pack §3", "several members lean introverted").
 * The safety judge verifies each trace back to a real input field, so the raw
 * text is authoritative and MUST NOT change — this is a pure DISPLAY remap.
 *
 * Brook & Ronnie (via the finalized `ai_note_source_labels` sheet, client_label
 * column) want the chips labeled by the underlying SOURCE FIELD, not the AI's
 * per-session wording — "Participant Matching Form", "Your Prep Notes", etc. The
 * source field is a finite ~30-item list; the AI's wording is not. So we key off
 * a stable signature the trace carries for each field and return the client
 * label. Anything we can't resolve is DROPPED (returns '') so the chip row is a
 * controlled vocabulary — only the approved labels ever show. Since those ~30
 * fields are the whole universe the AI is given, a miss is either a rare phrasing
 * or something outside the set; showing its raw text re-introduces the machine-ish
 * wording the client asked us to remove. A missing chip beats a garbage chip.
 *
 * ponytail: substring/word-boundary matching over the raw trace, not a wire
 * schema change. Deliberate — the AC1 comment committed to a render-only change
 * with "no backend or judge impact"; forcing the model to emit a stable enum is
 * the reliable-but-heavy alternative we explicitly did NOT take. Needles are kept
 * BROAD (one per field family, e.g. "segment" for the whole agenda family) rather
 * than exhaustive — we'd rather drop a rare long-tail phrasing than maintain a
 * fragile list of every way the model might word a source.
 */

/** MED-1065 — coach-readable provenance label (presentation-only). Also the
 *  fallback when a trace carries no recognizable source-field signature. */
export function humanizeSource(source: string): string {
  return source
    .replace(/\s*§\S*/g, '') // drop pack section refs: "§3", "§2.4", "§Close"
    .replace(/\s+pack\b/gi, '') // drop the "pack" qualifier on knowledge-pack ids
    .replace(/_/g, ' ') // snake_case field tokens → spaced words
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Ordered signature → client_label rules; FIRST match wins, so order encodes the
 * two real collisions:
 *   1. `prep_checklist.noted_carryover` contains "carryover" but is Prep Notes —
 *      so "prep checklist" is tested BEFORE the carryover rule.
 *   2. `prior_session.feedback_responses.carryover_for_next_session` is the
 *      carryover bucket, not feedback — so "carryover" is tested BEFORE "feedback".
 * Every other overlap is between rules that share a label, so it's harmless.
 * Needles are matched with word boundaries against the humanized (spaced,
 * lower-cased) trace.
 */
const RULES: ReadonlyArray<{ needles: string[]; label: string }> = [
  // --- ordering-critical (see docblock) ---
  { needles: ['prep checklist'], label: 'Your Prep Notes' },
  { needles: ['carryover'], label: 'Your Session Notes' },
  { needles: ['feedback responses', 'feedback'], label: 'Your Session Feedback' },
  { needles: ['engagement'], label: 'Session Engagement Scores' },
  // prior-session identity + full session details resolve to the same bucket, so
  // "session details" is safely tested before the narrower "session number".
  { needles: ['prior session', 'which session', 'session details', 'session title', 'session.title'], label: 'Session Details' },
  { needles: ['session number'], label: 'Session Number' },

  // --- participant matching-form signals ---
  { needles: ['primary goal'], label: 'Participant Matching Form' },
  { needles: ['development goal'], label: 'Participant Matching Form' },
  { needles: ['ie preference', 'i vs e', 'i vs. e', 'introvert', 'introverted', 'extrovert', 'extroverted'], label: 'Participant Matching Form' },
  { needles: ['experience'], label: 'Participant Matching Form' },
  { needles: ['group commentary', 'why this group'], label: 'Participant Matching Form' },

  // --- participant background ---
  { needles: ['role'], label: 'Participant Background' },
  { needles: ['location', 'office'], label: 'Participant Background' },
  { needles: ['business unit', 'personality type'], label: 'Participant Background' },

  // --- cohort / program ---
  { needles: ['participant description'], label: 'Cohort Details' },
  { needles: ['partner name', 'partner description'], label: 'Program Name' },
  { needles: ['cohort type'], label: 'Program Details' },

  // --- session agenda / curriculum ---
  // One BROAD needle covers the whole family — active_segment, segment_blocks,
  // segment_callouts and session_shape ("all_segments") all carry "segment" (or
  // the AI names "agenda" directly). Broad-not-exhaustive on purpose: a verbose
  // phrasing like "active segment title and block (one-word check-in frame)"
  // still resolves to Session Agenda instead of dropping as an outlier. No other
  // source field's trace uses the word "segment"/"agenda", so it can't collide.
  { needles: ['segment', 'segments', 'agenda'], label: 'Session Agenda' },

  // --- coach-private context ---
  { needles: ['leader notes'], label: 'Your Prep Notes' },
  { needles: ['concern flag', 'concern flags'], label: 'Your Session Notes' },
  { needles: ['internal notes'], label: 'Your Session Notes' },
  // attendance is the one in-scope field whose canonical AI trace is PROSE with no
  // structural token — the recipe emits "Marcus missed the last two sessions". A few
  // attendance-specific words map that prose to the chip instead of dropping it; none
  // appear in any other source field's trace, so they can't mint a false chip.
  { needles: ['attendance', 'attended', 'missed', 'absent', 'no-show', 'no show'], label: 'Session Attendance' },
  { needles: ['group notes'], label: 'Group Matching Notes' },
  { needles: ['coach profile', 'expertise', 'specialization', 'specializations', 'first time'], label: 'Your Coach Profile' },

  // --- Medley method packs ---
  { needles: ['medley voice'], label: 'Medley Coaching Guidance' },
  { needles: ['powerful questions'], label: 'Medley Coaching Guidance' },
  { needles: ['holding space'], label: 'Medley Coaching Guidance' },
  { needles: ['session anatomy'], label: 'Medley Coaching Guidance' },
];

const escape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Precompile: one word-boundary regex per rule (needles OR-ed together).
const COMPILED: ReadonlyArray<{ re: RegExp; label: string }> = RULES.map(r => ({
  re: new RegExp(`\\b(?:${r.needles.map(escape).join('|')})\\b`),
  label: r.label,
}));

/**
 * Resolve one raw source trace to its client-approved category label, or fall
 * back to the humanized trace when no source-field signature is recognized.
 * Returns '' for a trace that humanizes to nothing (caller filters it out).
 */
export function resolveSourceLabel(raw: string): string {
  const humanized = humanizeSource(raw);
  if (!humanized) return '';
  // Match ONLY the field-signature — the part before the first ':'. The value
  // after a colon is arbitrary free text the AI authored: a segment/session
  // TITLE ("segment purpose: Giving Effective Feedback") or a goal sentence
  // ("development_goal: give better feedback to reports"). Matching the whole
  // string lets a trigger word in that VALUE ("feedback", "role", "engagement")
  // fire an unrelated rule and mislabel the chip. The field key alone is the
  // signal. The full humanized string is still what we DISPLAY on fallback.
  const probe = humanized.split(':')[0].toLowerCase();
  for (const { re, label } of COMPILED) {
    if (re.test(probe)) return label;
  }
  // Outside the approved client set → DROP the chip (caller filters '' out; a note
  // whose sources are all unrecognized simply shows no "Based on" row). The ~30
  // source fields ARE the whole universe the AI is given, so a miss is either a
  // rare phrasing the broad needles don't catch or something outside the set —
  // either way, showing the raw humanized trace re-introduced the machine-ish
  // wording the client asked us to remove and read as a visual outlier. A missing
  // chip is cleaner than a garbage chip.
  return '';
}

/** Resolve a list of traces to display labels, dropping empties and de-duping
 *  the coarse category labels (two participant fields both map to one chip). */
export function resolveSourceLabels(sources: string[]): string[] {
  return [...new Set(sources.map(resolveSourceLabel).filter(Boolean))];
}
